// ABOUTME: Orchestration hook for the Quantity entity — combines config query, mutations, and form state.
// ABOUTME: Derives per-field override flags (diff vs. parent + already-overridden) so only overridden fields are saved.

import { useCallback, useMemo, useRef } from 'react'

import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { useQuantityConfig } from './useQuantityConfig'
import { useCreateQuantity, useCreateQuantityWithScope, useDeleteQuantity, useUpdateQuantity } from './useQuantityMutations'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'
import {
  QuantityConfig,
  QuantityFormValues,
  QuantityOverrides,
  QuantityUomIds,
  QuantityValues,
} from '../types/quantity'

interface UseQuantityEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
  /** Catalog availability — at root these gate which sections must be force-overridden. */
  fullLoadsAvailable?: boolean
  loadBalanceAvailable?: boolean
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: QuantityFormValues) => void
}

interface UseQuantityEntityResult {
  config: QuantityConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

const fieldDiffers = (a: unknown, b: unknown): boolean =>
  typeof a === 'object' ? JSON.stringify(a) !== JSON.stringify(b) : a !== b

/**
 * Determines which fields are overridden at this scope by comparing submitted form values against
 * the inherited parent values. A field is overridden only when its value differs from the parent —
 * pre-existing override flags are intentionally ignored so that reverting a field to the parent
 * value correctly produces hasAnyOverride === false and triggers the delete path. Exported for testing.
 */
export const computeQuantityOverrides = (
  values: QuantityFormValues,
  config: QuantityConfig
): QuantityOverrides => {
  const parent = config.parentValues
  const diff = (key: keyof QuantityValues): boolean =>
    fieldDiffers(values[key], parent[key])

  return {
    uom: values.uomId !== parent.uomId,
    default: diff('default'),
    min: diff('min'),
    max: diff('max'),
    maxOrderConfirmable: diff('maxOrderConfirmable'),
    step: diff('step'),
    shortLoadThreshold: diff('shortLoadThreshold'),
    numberOfDecimals: diff('numberOfDecimals'),
    roundToChangeIncrement: diff('roundToChangeIncrement'),
    fullLoadsEnabled: diff('fullLoadsEnabled'),
    fullLoadsUom: values.fullLoadsUomId !== parent.fullLoadsUomId,
    fullLoadsOptions: diff('fullLoadOptions'),
    loadBalanceEnabled: diff('loadBalanceEnabled'),
    minEligibleQuantity: diff('minEligibleQuantity'),
    maxEligibleQuantity: diff('maxEligibleQuantity'),
  }
}

export const useQuantityEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
  fullLoadsAvailable,
  loadBalanceAvailable,
}: UseQuantityEntityParams): UseQuantityEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, error } = useQuantityConfig(
    countryId,
    businessLine,
    configScopeId,
    rootScopeId
  )
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({
    uom: cfg.uom,
    uomId: cfg.uomId,
    default: cfg.default,
    min: cfg.min,
    max: cfg.max,
    maxOrderConfirmable: cfg.maxOrderConfirmable,
    step: cfg.step,
    shortLoadThreshold: cfg.shortLoadThreshold,
    numberOfDecimals: cfg.numberOfDecimals,
    roundToChangeIncrement: cfg.roundToChangeIncrement,
    fullLoadsEnabled: cfg.fullLoadsEnabled,
    fullLoadsUom: cfg.fullLoadsUom,
    fullLoadsUomId: cfg.fullLoadsUomId,
    fullLoadOptions: cfg.fullLoadOptions,
    loadBalanceEnabled: cfg.loadBalanceEnabled,
    minEligibleQuantity: cfg.minEligibleQuantity,
    maxEligibleQuantity: cfg.maxEligibleQuantity,
  })), [rawConfig, pendingScope])

  const createMutation = useCreateQuantity(countryId, businessLine)
  const createWithScopeMutation = useCreateQuantityWithScope(countryId, businessLine)
  const updateMutation = useUpdateQuantity(countryId, businessLine)
  const deleteMutation = useDeleteQuantity(countryId, businessLine)

  const formValuesRef = useRef<QuantityFormValues | null>(null)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  pendingScopeRef.current = pendingScope

  const handleValuesChange = useCallback((values: QuantityFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!formValuesRef.current || !config) return
      const values = formValuesRef.current
      const baseOverrides = computeQuantityOverrides(values, config)
      // Root scope has no parent to inherit from: the backend requires every applicable field to be
      // overridden, so force the required flags on regardless of whether the value differs from defaults.
      const atRoot = scopeId !== undefined && scopeId === rootScopeId
      const overrides = atRoot
        ? {
            ...baseOverrides,
            uom: true,
            default: true,
            min: true,
            max: true,
            step: true,
            shortLoadThreshold: true,
            numberOfDecimals: true,
            roundToChangeIncrement: true,
            fullLoadsEnabled: fullLoadsAvailable ? true : baseOverrides.fullLoadsEnabled,
            fullLoadsUom: fullLoadsAvailable && values.fullLoadsEnabled ? true : baseOverrides.fullLoadsUom,
            fullLoadsOptions:
              fullLoadsAvailable && values.fullLoadsEnabled ? true : baseOverrides.fullLoadsOptions,
            loadBalanceEnabled: loadBalanceAvailable ? true : baseOverrides.loadBalanceEnabled,
            minEligibleQuantity:
              loadBalanceAvailable && values.loadBalanceEnabled ? true : baseOverrides.minEligibleQuantity,
            maxEligibleQuantity:
              loadBalanceAvailable && values.loadBalanceEnabled ? true : baseOverrides.maxEligibleQuantity,
          }
        : baseOverrides
      const hasAnyOverride = Object.values(overrides).some(Boolean)
      const scopeToCreate = pendingScopeRef.current

      if (scopeToCreate) {
        if (!hasAnyOverride) {
          onSuccess({})
          return
        }
        const uomIds: QuantityUomIds = {
          uomId: values.uomId,
          fullLoadsUomId: values.fullLoadsUomId,
        }
        createWithScopeMutation.mutate({ scope: scopeToCreate, values, overrides, uomIds }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
        return
      }

      if (!scopeId) return

      if (!hasAnyOverride && scopeId !== rootScopeId) {
        if (config.id) {
          deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
        } else {
          onSuccess({ wasDelete: true })
        }
        return
      }

      const uomIds: QuantityUomIds = {
        uomId: values.uomId,
        fullLoadsUomId: values.fullLoadsUomId,
      }
      const isNew = !config.id
      const mutate = isNew ? createMutation.mutate : updateMutation.mutate
      mutate({ scopeId, values, overrides, uomIds }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, rootScopeId, config, fullLoadsAvailable, loadBalanceAvailable, createMutation.mutate, createWithScopeMutation.mutate, updateMutation.mutate, deleteMutation.mutate]
  )

  const handleReset = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!scopeId) return
      deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, deleteMutation.mutate]
  )

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving =
    createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return { config, isLoading, error, isSaving, actions }
}
